/**
 * Question Format Service
 * Handles formatting and processing of question text and clues
 */

/**
 * Extract clues from formatted question text
 * @param {string} formattedText - Question text with clue markup
 * @returns {string[]} - Array of extracted clue texts
 */
const extractClues = (formattedText) => {
  if (!formattedText) return [];
  
  const matches = [];
  
  // Extract clues from <clue> tags
  const clueRegex = /<clue>(.*?)<\/clue>/g;
  let match;
  while ((match = clueRegex.exec(formattedText)) !== null) {
    matches.push(match[1]);
  }
  
  // Extract clues from [text] format
  const bracketRegex = /\[([^\]]+)\]/g;
  while ((match = bracketRegex.exec(formattedText)) !== null) {
    matches.push(match[1]);
  }
  
  return matches;
};

/**
 * Get question body with clues highlighted if showClues is true
 * @param {Object} question - Question object from API
 * @param {boolean} showClues - Whether to show highlighted clues
 * @returns {string} - Formatted question text with or without highlighted clues
 */
const getQuestionWithClues = (question, showClues = false) => {
  if (!question) return "";
  
  // Get the initial text content from the question object
  // If showClues is true, we can use the pre-formatted version if available
  // If showClues is false, we should NOT use formattedWithClues as it contains highlighted clues
  let formattedText;
  // Determine which text source to use with detailed logging
  // console.log(`[QuestionFormatService] getQuestionWithClues called with showClues=${showClues}, questionId=${question.id || 'unknown'}`);
  
  // CRITICAL: We must strictly respect the showClues parameter
  // When showClues is false, we must NEVER use formattedWithClues content
  if (showClues && question.formattedWithClues) {
    formattedText = question.formattedWithClues;
    // console.log('[QuestionFormatService] Using pre-formatted clues text from formattedWithClues');
  } else {
    // When not showing clues, always use the non-clue version
    if (question.formattedWithoutClues) {
      formattedText = question.formattedWithoutClues;
      // console.log('[QuestionFormatService] Using pre-formatted non-clue text from formattedWithoutClues');
    } else {
      // Fall back to base content if no pre-formatted version exists
      formattedText = question.bodyFormatted || 
                      question.body || 
                      (typeof question.question === 'string' ? question.question : '') ||
                      (question.question?.bodyFormatted || question.question?.body || '') || 
                      '';
      // console.log(`[QuestionFormatService] Using base text: ${formattedText.substring(0, 30)}...`);
    }
  }
  
  // console.log(`[QuestionFormatService] Text source decision: showClues=${showClues}, hasFormattedWithClues=${!!question.formattedWithClues}, hasBodyFormatted=${!!question.bodyFormatted}`);
  
  // If no text content was found, log an error and return empty string
  if (!formattedText) {
    console.warn('No text content found in question object:', question);
    return '';
  }// If showing clues, apply highlighting using CSS classes for proper theme support
  if (showClues) {
    // Highlight <clue> tags with CSS class
    if (formattedText.includes('<clue>')) {
      formattedText = formattedText.replace(/<clue>(.*?)<\/clue>/g, (match, content) => {
        return `<span class="clue-highlight">${content}</span>`;
      });
    }
    
    // Highlight [text] format clues with CSS class
    formattedText = formattedText.replace(/\[([^\]]+)\]/g, (match, content) => {
      return `<span class="clue-highlight">${content}</span>`;
    });
  } else {
    // When not showing clues, just show the content without any formatting
    formattedText = formattedText.replace(/<clue>(.*?)<\/clue>/g, '$1');
    formattedText = formattedText.replace(/\[([^\]]+)\]/g, '$1');
  }

  return formattedText;
};

/**
 * Get the correct answer ID from a question object
 * @param {Object} question - Question object from API
 * @returns {number|null} - ID of the correct answer or null if not found
 */
const getCorrectAnswerId = (question) => {
  if (question && question.answers && Array.isArray(question.answers)) {
    const correctAnswer = question.answers.find(answer => answer.isCorrect);
    return correctAnswer ? correctAnswer.id : null;
  }
  
  return null;
};

/**
 * Format answer options in a consistent way
 * @param {Object} question - Question object from API
 * @returns {string[]} - Array of formatted answer options
 */
const getFormattedOptions = (question) => {
  // console.log('getFormattedOptions called with question:', 
    // question ? `ID: ${question.id || question.questionId || 'unknown'}, Keys: ${Object.keys(question).join(', ')}` : 'null');
  
  if (!question) return [];
  
  // Case 1: API format with answers array that has answer property
  if (question.answers && Array.isArray(question.answers)) {
    // console.log('Using API question format with answers array');
    return question.answers.map((answer, index) => {
      const letter = String.fromCharCode(65 + index); // A, B, C, D, etc.
      const answerText = answer.answer || answer.text || answer.content || '';
      return `${letter}. ${answerText}`;
    });
  }
  
  // Case 2: Direct options array from mock data
  if (question.options && Array.isArray(question.options)) {
    // console.log('Using mock question format with options array');
    return question.options;
  }
  
  // Case 3: If question has a 'question' object field that has answers
  if (question.question && typeof question.question === 'object' && 
      question.question.answers && Array.isArray(question.question.answers)) {
    // console.log('Using nested question.answers array');
    return question.question.answers.map((answer, index) => {
      const letter = String.fromCharCode(65 + index);
      const answerText = answer.answer || answer.text || answer.content || '';
      return `${letter}. ${answerText}`;
    });
  }
  
  // Try to extract from unknown format
  console.warn('Unknown question format, trying to find answer options in properties');
  for (const key in question) {
    const value = question[key];
    if (Array.isArray(value) && value.length > 0 && 
        (key.includes('answer') || key.includes('option') || key === 'choices')) {
      // console.log(`Found potential answers in '${key}' property`);
      return value.map((item, index) => {
        const letter = String.fromCharCode(65 + index);
        let text = '';
        if (typeof item === 'string') {
          text = item;
        } else if (typeof item === 'object') {
          text = item.text || item.answer || item.content || JSON.stringify(item);
        }
        return `${letter}. ${text}`;
      });
    }
  }
  
  // No options found
  console.error('No answer options found in question data');
  return [];
};

/**
 * Extract the explanation text from a question
 * @param {Object} question - Question object from API
 * @returns {string} - Explanation text for the question
 */
const getExplanationText = (question) => {
  if (!question) return "No explanation available";
  
  try {
    // For API data questions - check if we have answers with explanations
    if (question.answers && Array.isArray(question.answers)) {
      // First look for the correct answer's explanation
      const correctAnswer = question.answers.find(answer => answer.isCorrect);
      
      if (correctAnswer && typeof correctAnswer.explanation === 'string' && correctAnswer.explanation.trim()) {
        return correctAnswer.explanation;
      }
      
      // If no explanation in correct answer, check all answers for explanations
      for (const answer of question.answers) {
        if (answer && typeof answer.explanation === 'string' && answer.explanation.trim()) {
          return answer.explanation;
        }
      }
    }
    
    // Check explanations directly on the question object
    const explanationFields = ['explanation', 'explanationFormatted', 'feedback', 'reasoning'];
    for (const field of explanationFields) {
      if (question[field]) {
        if (typeof question[field] === 'string' && question[field].trim()) {
          return question[field];
        } else if (typeof question[field] === 'object') {
          // Try to find text inside the explanation object
          const obj = question[field];
          const textFields = ['text', 'content', 'value', 'body', 'formatted'];
          
          for (const textField of textFields) {
            if (typeof obj[textField] === 'string' && obj[textField].trim()) {
              return obj[textField];
            }
          }
        }
      }
    }
    
    // Check if there's a reference field that might provide context
    if (question.reference && typeof question.reference === 'string') {
      return `Reference: ${question.reference}`;
    }
    
    // For explanation objects in different formats, try to stringify reasonably
    for (const field of explanationFields) {
      if (question[field] && typeof question[field] === 'object') {
        try {
          return JSON.stringify(question[field], null, 2);
        } catch (e) {
          console.warn(`Could not stringify ${field}:`, e);
        }
      }
    }
    
    return "No explanation available";
  } catch (e) {
    console.error("Error getting explanation:", e);
    return "Error retrieving explanation";
  }
};

/**
 * Format a question from API response into a consistent structure
 * @param {Object} questionData - Raw question data from API
 * @returns {Object} - Consistently formatted question object
 */
const formatQuestionFromResponse = (questionData) => {
  if (!questionData) {
    console.warn('Called formatQuestionFromResponse with null/undefined questionData');
    return null;
  }

  // console.log('Formatting question data:', JSON.stringify(questionData, null, 2));
  
  // Enhanced structure detection and normalization
  // First case: New API structure with nested question object
  if (questionData && questionData.question) {
    // console.log('Detected new API structure with nested question object');
    return {
      id: questionData.id, // This is the examQuestionID
      examId: questionData.examId,
      questionId: questionData.questionId,
      body: questionData.question.body || questionData.body || "",
      bodyFormatted: questionData.question.bodyFormatted || questionData.bodyFormatted || "",
      reference: questionData.question.reference || questionData.reference || "",
      answers: questionData.question.answers || questionData.answers || [],
      timeSpent: questionData.timeSpent || 0,
      isFlagged: questionData.isFlagged || false,
      isCorrect: questionData.isCorrect || false,
      // Ensure we have a flag to identify real data vs mock
      isRealApiData: true
    };
  }
  
  // Second case: Flatter API structure with question properties at top level
  if (questionData && (questionData.body || questionData.bodyFormatted)) {
    // console.log('Detected flatter API structure with top-level question properties');
    return {
      id: questionData.id || questionData.questionId,
      questionId: questionData.questionId || questionData.id,
      body: questionData.body || "",
      bodyFormatted: questionData.bodyFormatted || questionData.body || "",
      reference: questionData.reference || "",
      answers: questionData.answers || [],
      timeSpent: questionData.timeSpent || 0,
      isFlagged: questionData.isFlagged || false,
      isCorrect: questionData.isCorrect || false,
      // Ensure we have a flag to identify real data vs mock
      isRealApiData: true
    };
  }
  
  // Third case: Legacy mock question structure
  if (questionData && questionData.question && typeof questionData.question === 'string') {
    // console.log('Detected legacy mock question structure');
    return {
      body: questionData.question,
      bodyFormatted: questionData.question,
      reference: questionData.reference || "",
      options: questionData.options || [],
      correctAnswer: questionData.correctAnswer,
      explanation: questionData.explanation || "",
      // Flag to identify mock data
      isRealApiData: false
    };
  }
  
  // Return the data as is if it doesn't match any known structure
  // but add the real API data flag
  // console.log('Unknown question structure, returning as-is');
  return {
    ...questionData,
    isRealApiData: true
  };
};

export default {
  extractClues,
  getQuestionWithClues,
  getCorrectAnswerId,
  getFormattedOptions,
  getExplanationText,
  formatQuestionFromResponse
};
